import { useCallback, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  usePayrollDailyReport,
  usePayrollProjectsReport,
  useDownloadPayrollDailyReportXlsx,
  useDownloadPayrollProjectsReportXlsx,
} from '../../hooks/useReports';
import { getAdminUsers, getEmployeeTypes } from '../../services/api/admin';
import ReportFilters from '../../components/domain/ReportFilters';
import { Button } from '../../components/common/Button';
import { Spinner } from '../../components/common/Spinner';
import type { PayrollDailyRow, PayrollProjectsRow } from '../../services/api/reports';

type TabId = 'days' | 'projects';

function formatDateDDMMYYYY(dateStr: string): string {
  const [year, month, day] = dateStr.split('-');
  return `${day}.${month}.${year}`;
}

function PayrollDailyView({ rows }: { rows: PayrollDailyRow[] }) {
  if (rows.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-sm text-gray-500">
        No data available for the selected filters.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {rows.map((row) => (
        <div key={row.userId} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr className="bg-gray-50">
                  <th colSpan={7} className="px-6 py-3 text-left text-sm font-semibold text-gray-900">
                    {row.userName}
                  </th>
                </tr>
                <tr className="bg-gray-50">
                  <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="w-4"></th>
                  <th className="px-6 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Work hours</th>
                  <th className="w-4"></th>
                  <th className="px-6 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Vacation (personal)</th>
                  <th className="w-4"></th>
                  <th className="px-6 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Non-paid time off</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {row.dailyBreakdown.map((day) => (
                  <tr key={day.date} className="hover:bg-gray-50">
                    <td className="px-6 py-2 text-sm text-gray-900 whitespace-nowrap">{formatDateDDMMYYYY(day.date)}</td>
                    <td className="w-4"></td>
                    <td className="px-6 py-2 text-sm text-gray-700 text-right whitespace-nowrap">
                      {day.workHours > 0 ? day.workHours.toFixed(1) : ''}
                    </td>
                    <td className="w-4"></td>
                    <td className="px-6 py-2 text-sm text-gray-700 text-right whitespace-nowrap">
                      {day.vacationHours > 0 ? day.vacationHours.toFixed(1) : ''}
                    </td>
                    <td className="w-4"></td>
                    <td className="px-6 py-2 text-sm text-gray-700 text-right whitespace-nowrap">
                      {day.nonPaidHours > 0 ? day.nonPaidHours.toFixed(1) : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-blue-50 border-t border-gray-200">
                  <td className="px-6 py-2 text-sm font-semibold text-gray-900">Total</td>
                  <td className="w-4"></td>
                  <td className="px-6 py-2 text-sm font-semibold text-gray-900 text-right">
                    {row.totalWorkHours > 0 ? row.totalWorkHours.toFixed(1) : ''}
                  </td>
                  <td className="w-4"></td>
                  <td className="px-6 py-2 text-sm font-semibold text-gray-900 text-right">
                    {row.totalVacationHours > 0 ? row.totalVacationHours.toFixed(1) : ''}
                  </td>
                  <td className="w-4"></td>
                  <td className="px-6 py-2 text-sm font-semibold text-gray-900 text-right">
                    {row.totalNonPaidHours > 0 ? row.totalNonPaidHours.toFixed(1) : ''}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}

function PayrollProjectsView({ rows }: { rows: PayrollProjectsRow[] }) {
  if (rows.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-sm text-gray-500">
        No data available for the selected filters.
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full">
          {rows.map((row, rowIdx) => (
            <tbody key={row.userId}>
              {rowIdx > 0 && (
                <tr><td colSpan={6} className="h-3 border-t border-gray-200"></td></tr>
              )}
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">{row.userName}</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap w-0">Hours</th>
                <th className="w-4"></th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap w-0">Invoiced hours</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap w-0">Is Billable?</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap w-0">Invoice</th>
              </tr>
              {row.projects.map((proj) => (
                <tr key={proj.projectName} className="hover:bg-gray-50 border-b border-gray-100">
                  <td className="px-6 py-2 text-sm text-gray-900">{proj.projectName}</td>
                  <td className="px-6 py-2 text-sm text-gray-700 text-right whitespace-nowrap">
                    {proj.hours.toFixed(2).replace('.', ',')}
                  </td>
                  <td className="w-4"></td>
                  <td className="px-6 py-2 text-sm text-gray-700 text-right whitespace-nowrap">
                    {proj.invoicedHours.toFixed(2).replace('.', ',')}
                  </td>
                  <td className="px-6 py-2 text-sm text-gray-700 whitespace-nowrap">{proj.billable ? 'Yes' : 'No'}</td>
                  <td className="px-6 py-2 text-sm whitespace-nowrap">
                    {proj.invoices.map((inv, i) => (
                      <span key={inv.id}>
                        {i > 0 && ', '}
                        <Link to={`/invoices/${inv.id}`} className="text-primary-600 hover:text-primary-800 hover:underline">
                          {inv.invoiceNumber || `#${inv.id}`}
                        </Link>
                      </span>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          ))}
        </table>
      </div>
    </div>
  );
}

function parseUserIds(str: string | null): number[] {
  if (!str) return [];
  return str.split(',').map((s) => parseInt(s, 10)).filter((n) => !isNaN(n));
}

export default function PayrollReportPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Read initial state from URL
  const urlTab = searchParams.get('tab');
  const urlFrom = searchParams.get('from');
  const urlTo = searchParams.get('to');
  const urlUserIds = searchParams.get('userIds');

  const [activeTab, setActiveTabState] = useState<TabId>(urlTab === 'projects' ? 'projects' : 'days');
  const [dateFilters, setDateFiltersState] = useState<{ from: string; to: string } | null>(
    urlFrom && urlTo ? { from: urlFrom, to: urlTo } : null
  );
  const [selectedUserIds, setSelectedUserIdsState] = useState<number[]>(parseUserIds(urlUserIds));
  const [userSearch, setUserSearch] = useState('');
  const [employeeTypeFilter, setEmployeeTypeFilter] = useState<number | ''>('');

  const updateSearchParams = useCallback((updates: Record<string, string | null>) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [key, val] of Object.entries(updates)) {
        if (val === null || val === '') {
          next.delete(key);
        } else {
          next.set(key, val);
        }
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const setActiveTab = (tab: TabId) => {
    setActiveTabState(tab);
    updateSearchParams({ tab: tab === 'days' ? null : tab });
  };

  const setDateFilters = (filters: { from: string; to: string }) => {
    setDateFiltersState(filters);
    updateSearchParams({ from: filters.from, to: filters.to });
  };

  const setSelectedUserIds = (updater: number[] | ((prev: number[]) => number[])) => {
    setSelectedUserIdsState((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      updateSearchParams({ userIds: next.length > 0 ? next.join(',') : null });
      return next;
    });
  };

  const { data: users } = useQuery({
    queryKey: ['adminUsers'],
    queryFn: () => getAdminUsers(),
  });
  const { data: employeeTypes } = useQuery({
    queryKey: ['employeeTypes'],
    queryFn: () => getEmployeeTypes(),
  });

  const filters = dateFilters ? { ...dateFilters, userIds: selectedUserIds.length > 0 ? selectedUserIds : undefined } : null;
  const { data: dailyReport, isLoading: isLoadingDaily, error: dailyError } = usePayrollDailyReport(
    activeTab === 'days' ? filters : null
  );
  const { data: projectsReport, isLoading: isLoadingProjects, error: projectsError } = usePayrollProjectsReport(
    activeTab === 'projects' ? filters : null
  );

  const downloadDailyXlsx = useDownloadPayrollDailyReportXlsx();
  const downloadProjectsXlsx = useDownloadPayrollProjectsReportXlsx();

  const isLoading = activeTab === 'days' ? isLoadingDaily : isLoadingProjects;
  const error = activeTab === 'days' ? dailyError : projectsError;

  const toggleUser = (id: number) => {
    setSelectedUserIds((prev) =>
      prev.includes(id) ? prev.filter((uid) => uid !== id) : [...prev, id]
    );
  };

  // MKS-119: payroll covers employees only — exclude freelancers/contractors.
  // `category === 'contractor'` is the codebase-canonical freelancer flag
  // (backend employeeTypeCategory.ts isContractorType). Users with a null or
  // 'employee' category are kept.
  const contractorTypeIds = new Set(
    (employeeTypes ?? []).filter((et) => et.category === 'contractor').map((et) => et.id)
  );
  const activeUsers =
    users
      ?.filter((u) => !u.deletedAt)
      .filter((u) => !(u.employeeTypeId != null && contractorTypeIds.has(u.employeeTypeId))) ?? [];
  const filteredUsers = activeUsers
    .filter((u) => {
      if (employeeTypeFilter !== '' && u.employeeTypeId !== employeeTypeFilter) return false;
      if (!userSearch) return true;
      const name = [u.firstName, u.lastName].filter(Boolean).join(' ').toLowerCase();
      return name.includes(userSearch.toLowerCase()) || u.email.toLowerCase().includes(userSearch.toLowerCase());
    })
    .sort((a, b) => (a.lastName ?? '').localeCompare(b.lastName ?? '') || (a.firstName ?? '').localeCompare(b.firstName ?? ''));

  const tabs: { id: TabId; label: string }[] = [
    { id: 'days', label: 'Days' },
    { id: 'projects', label: 'Projects' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <button onClick={() => navigate('/reports')} className="text-sm text-gray-500 hover:text-gray-700 mb-1">
          &larr; Back to Reports
        </button>
        <h1 className="text-2xl font-semibold text-gray-900">Payroll Report</h1>
      </div>

      <ReportFilters onApply={setDateFilters} isLoading={isLoading} initialFrom={urlFrom || undefined} initialTo={urlTo || undefined} />

      {/* User Filter */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-700">
            Users {selectedUserIds.length > 0 && `(${selectedUserIds.length} selected)`}
          </label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setSelectedUserIds(filteredUsers.map((u) => u.id))}
              className="text-sm text-primary-600 hover:text-primary-800"
            >
              Select all
            </button>
            <button
              type="button"
              onClick={() => setSelectedUserIds([])}
              className="text-sm text-primary-600 hover:text-primary-800"
            >
              Deselect all
            </button>
          </div>
        </div>
        <div className="border border-gray-200 rounded-lg">
          <div className="p-2 border-b border-gray-200 flex gap-2">
            <input
              type="text"
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              placeholder="Filter users..."
              className="flex-1 rounded border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <select
              value={employeeTypeFilter}
              onChange={(e) => setEmployeeTypeFilter(e.target.value ? Number(e.target.value) : '')}
              className="rounded border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">All types</option>
              {employeeTypes
                ?.filter((et) => et.category !== 'contractor')
                .map((et) => (
                  <option key={et.id} value={et.id}>{et.name}</option>
                ))}
            </select>
          </div>
          <div className="max-h-48 overflow-y-auto p-2 space-y-1">
            {filteredUsers.map((user) => (
              <label key={user.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedUserIds.includes(user.id)}
                  onChange={() => toggleUser(user.id)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-900">
                  {[user.lastName, user.firstName].filter(Boolean).join(' ') || user.email}
                </span>
              </label>
            ))}
            {filteredUsers.length === 0 && (
              <p className="text-sm text-gray-500 px-2 py-1">No users found.</p>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex -mb-px">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-3 text-sm font-medium text-center border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-sm text-red-800">{(error as Error).message}</p>
        </div>
      )}

      {isLoading && (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      )}

      {activeTab === 'days' && dailyReport && !isLoading && (
        <>
          <div className="flex justify-end">
            <Button
              variant="secondary"
              onClick={() => filters && downloadDailyXlsx.mutate(filters)}
              isLoading={downloadDailyXlsx.isPending}
              disabled={!filters}
            >
              Download xlsx
            </Button>
          </div>
          <PayrollDailyView rows={dailyReport.rows} />
        </>
      )}

      {activeTab === 'projects' && projectsReport && !isLoading && (
        <>
          <div className="flex justify-end">
            <Button
              variant="secondary"
              onClick={() => filters && downloadProjectsXlsx.mutate(filters)}
              isLoading={downloadProjectsXlsx.isPending}
              disabled={!filters}
            >
              Download xlsx
            </Button>
          </div>
          <PayrollProjectsView rows={projectsReport.rows} />
        </>
      )}
    </div>
  );
}
